import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import type { CampaignRole } from '../model/performance.types';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import { linkedCampaignSchema } from './schemas';

export interface LinkCampaignInput {
  projectId: string;
  connectorId: string;
  externalCampaignId: string;
  name: string;
  channel: string;
  currency: string | null;
  role: CampaignRole;
}

/**
 * Invalidates both the campaign list and every performance window: a newly linked
 * campaign changes what every date range totals, and a role change can move an entire
 * platform's impressions from one row to another.
 */
export function useCampaignInvalidation(projectId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.campaigns(projectId) }),
      queryClient.invalidateQueries({
        queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.performanceAll(projectId),
      }),
    ]);
  };
}

export function useLinkCampaign(projectId: string) {
  const invalidate = useCampaignInvalidation(projectId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LinkCampaignInput) => {
      const res = await mediaHttp.post({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-campaigns',
        body: input,
      });
      return parseResponse(linkedCampaignSchema, res);
    },
    onSuccess: async () => {
      await Promise.all([
        invalidate(),
        // A linked campaign stops being an adoptable discovery. Not the
        // connector-campaigns list: the platform's own campaigns have not changed,
        // only which of them are already linked, and a freshly-mounted dialog
        // re-reads that anyway.
        queryClient.invalidateQueries({
          queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.discoveredCampaigns(projectId),
        }),
      ]);
    },
  });
}

export function useUnlinkCampaign(projectId: string) {
  const invalidate = useCampaignInvalidation(projectId);

  return useMutation({
    mutationFn: async (campaignId: string) => {
      await mediaHttp.delete({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-campaigns/${campaignId}`,
      });
    },
    onSuccess: invalidate,
  });
}

export function useUpdateCampaignRole(projectId: string) {
  const invalidate = useCampaignInvalidation(projectId);

  return useMutation({
    mutationFn: async (input: {
      campaignId: string;
      role: CampaignRole;
      counterpartCampaignId: string | null;
    }) => {
      await mediaHttp.patch({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-campaigns/${input.campaignId}`,
        body: { role: input.role, counterpartCampaignId: input.counterpartCampaignId },
      });
    },
    onSuccess: invalidate,
  });
}
